import { IconCopy, IconRepeat, IconTrash } from "@dream/ui";
import { type ReactNode, useState } from "react";
import { ColorSwatches } from "./color-swatches.tsx";

/**
 * Menu de contexto da frame Edit event (74:6203).
 *
 * Paleta de cor, reagendar, copiar, duplicar, excluir. “Make Flexible” do Cron
 * não entra: o arraste já é a flexibilidade do operacional.
 */

export interface EventContextMenuState {
  deliverableId: string;
  occurrenceStart: string;
  title: string;
  typeId: string;
  color: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  x: number;
  y: number;
}

export type RescheduleShift = "hour" | "tomorrow" | "nextWeek";

export function EventContextMenu({
  state,
  onOpen,
  onDuplicate,
  onDelete,
  onCopy,
  onColor,
  onReschedule,
  onClose,
}: {
  state: EventContextMenuState;
  onOpen: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onCopy: () => void;
  onColor: (color: string) => void;
  onReschedule: (shift: RescheduleShift) => void;
  onClose: () => void;
}) {
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        aria-label="Fechar menu da entrega"
        className="fixed inset-0 z-[55]"
        onClick={onClose}
        onContextMenu={(event) => {
          event.preventDefault();
          onClose();
        }}
      />
      <div
        role="menu"
        data-testid="event-context-menu"
        className="dream-mini-window dream-popover-in fixed z-[56] w-[15.5rem] p-1.5"
        style={{
          left: Math.min(state.x, window.innerWidth - 260),
          top: Math.min(state.y, window.innerHeight - 320),
        }}
      >
        <p className="truncate px-2.5 py-1.5 text-[12px] font-semibold text-[var(--color-ink-muted)]">
          {state.title}
        </p>
        {state.typeId !== "" && (
          <div className="px-2.5 py-2">
            <ColorSwatches value={state.color} onChange={onColor} ariaLabel="Cor da entrega" />
          </div>
        )}
        <MenuItem label="Abrir" hint="↵" onClick={onOpen} />
        <div className="relative">
          <MenuItem label="Reagendar" hint="›" onClick={() => setRescheduleOpen((open) => !open)} />
          {rescheduleOpen && (
            <div className="dream-mini-window absolute left-full top-0 z-[57] ml-1 w-[13.5rem] p-1.5">
              <MenuItem label="Daqui a 1 hora" onClick={() => onReschedule("hour")} />
              <MenuItem label="Amanhã" onClick={() => onReschedule("tomorrow")} />
              <MenuItem label="Próxima semana" onClick={() => onReschedule("nextWeek")} />
            </div>
          )}
        </div>
        <MenuItem
          label="Copiar título"
          hint="⌘C"
          icon={<IconCopy className="size-3.5" />}
          onClick={onCopy}
        />
        <MenuItem
          label="Duplicar"
          hint="⌘D"
          icon={<IconRepeat className="size-3.5" />}
          onClick={onDuplicate}
        />
        <div className="my-1 h-px bg-[var(--color-line)]" />
        <MenuItem
          label="Excluir"
          hint="⌫"
          tone="danger"
          icon={<IconTrash className="size-3.5" />}
          onClick={onDelete}
        />
      </div>
    </>
  );
}

function MenuItem({
  label,
  hint,
  onClick,
  icon,
  tone = "default",
}: {
  label: string;
  hint?: string;
  onClick: () => void;
  icon?: ReactNode;
  tone?: "default" | "danger";
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={[
        "flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] font-medium",
        tone === "danger"
          ? "text-[#AB3434] hover:bg-[#FEEDED]"
          : "text-[var(--color-ink)] hover:bg-[var(--color-surface-subtle)]",
      ].join(" ")}
    >
      {icon}
      <span className="min-w-0 flex-1">{label}</span>
      {hint !== undefined && (
        <span className="text-[11px] text-[var(--color-ink-muted)]">{hint}</span>
      )}
    </button>
  );
}
